import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useKioskMode } from '../../contexts/KioskModeContext';
import { useAppSelector } from '../../store/hooks';
import restaurantService from '../../services/restaurantService';
import { useGuestOrderType } from '../../hooks/useGuestOrderType';
import { requestCaptainAssistanceOpen } from '../../utils/captainPanelEvents';
import { withGuestOrderTypeQuery } from '../../utils/guestOrderTypeStorage';
import { kioskMenuPath, kioskOrdersPath, kioskOrderTypePath } from './kioskPaths';
import styles from './KioskChrome.module.css';

const KioskChrome: React.FC = () => {
  const { appName: paramApp } = useParams<{ appName: string }>();
  const {
    enabled,
    appName: ctxApp,
    restaurantName: bridgeName,
    role,
    tableId,
    tableNumber,
    orderTypes,
    awaitingFeedbackPhase,
    kioskGuestLogout,
  } = useKioskMode();
  const appName = paramApp || ctxApp;
  const navigate = useNavigate();
  const location = useLocation();
  const guestOrderType = useGuestOrderType(appName || undefined);
  const { isAuthenticated, authReady } = useAppSelector((s) => s.user);
  const guestSignedIn = authReady && isAuthenticated;
  const [name, setName] = useState(bridgeName || '');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !appName) return;
    if (bridgeName) setName(bridgeName);
    let cancelled = false;
    restaurantService
      .getRestaurant(appName)
      .then((res) => {
        if (cancelled) return;
        const r = res?.restaurant;
        if (r?.restaurantName) setName(r.restaurantName);
        if (r?.appLogo) setLogoUrl(r.appLogo);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [enabled, appName, bridgeName]);

  if (!enabled || !appName) {
    return null;
  }

  const handleDone = () => {
    if (!appName) return;
    kioskGuestLogout(appName, awaitingFeedbackPhase ? 'POST_ORDER' : 'NOT_YOU');
  };

  const openCallCaptain = () => {
    requestCaptainAssistanceOpen();
  };

  const isTable = role === 'TABLE_KIOSK';
  const tableBadgeLabel = tableNumber?.trim() || null;
  const showTableBadge = guestOrderType !== 3 && tableId != null && tableBadgeLabel != null;
  const canSwitchOrderType = orderTypes.length > 1 && !isTable;
  const kioskSubPath = location.pathname.replace(new RegExp(`^/kiosk/${appName}/?`), '');
  const onCartPage = kioskSubPath === 'cart' || kioskSubPath.startsWith('cart/');
  const onOrdersPage = kioskSubPath === 'orders' || kioskSubPath.startsWith('orders/');
  const showBackToMenu = !awaitingFeedbackPhase && (onCartPage || onOrdersPage);
  const goToMenu = () => {
    navigate(withGuestOrderTypeQuery(kioskMenuPath(appName), appName, guestOrderType));
  };

  return (
    <header className={styles.bar} role="banner">
      <div className={styles.brand}>
        {logoUrl ? (
          <img className={styles.logo} src={logoUrl} alt="" />
        ) : (
          <div className={styles.logo} aria-hidden />
        )}
        <div className={styles.brandText}>
          <div className={styles.nameRow}>
            <span className={styles.name}>{name || appName}</span>
            <span className={styles.badge}>Express Kiosk</span>
          </div>
        </div>
      </div>

      <div className={styles.center}>
        {showTableBadge ? (
          <div className={styles.tableBadge} aria-label={`Table ${tableBadgeLabel}`}>
            <span className={`material-symbols-outlined ${styles.tableBadgeIcon}`} aria-hidden>
              table_restaurant
            </span>
            <span className={styles.tableBadgeText}>Table {tableBadgeLabel}</span>
          </div>
        ) : null}
      </div>

      <div className={styles.actions}>
        {guestSignedIn && isTable && !awaitingFeedbackPhase ? (
          <div className={styles.tableActions}>
            <Link className={styles.action} to={kioskOrdersPath(appName)}>
              My orders
            </Link>
            <button type="button" className={styles.action} onClick={openCallCaptain}>
              Call Captain
            </button>
          </div>
        ) : null}
        {guestSignedIn && isTable && awaitingFeedbackPhase ? (
          <button type="button" className={styles.action} onClick={openCallCaptain}>
            Call Captain
          </button>
        ) : null}
        {showBackToMenu ? (
          <button type="button" className={styles.backMenu} onClick={goToMenu}>
            <span className="material-symbols-outlined" aria-hidden>
              arrow_back
            </span>
            Menu
          </button>
        ) : null}
        {canSwitchOrderType ? (
          <button
            type="button"
            className={styles.orderTypeBtn}
            onClick={() => navigate(kioskOrderTypePath(appName))}
          >
            Change service
          </button>
        ) : null}
        {guestSignedIn ? (
          <button
            type="button"
            className={styles.logout}
            onClick={handleDone}
            title={
              awaitingFeedbackPhase
                ? 'Sign out after your feedback'
                : 'Sign out so the next guest can use this kiosk'
            }
          >
            Logout
          </button>
        ) : null}
      </div>
    </header>
  );
};

export default KioskChrome;
